// Marketing email rendering: a campaign's blocks + theme → branded, email-client-safe HTML (inline
// styles, table layout, responsive stacking) + a plain-text part, personalised per recipient. Pure —
// no DB, network or Node APIs — so the browser canvas, the composer preview, the test send and the
// real send all produce exactly the same email (the client imports this file directly).
//
// Blocks: heading · text · button · image · split · columns · products · bestsellers · countdown ·
// promotion · lookbook · video · quote · social · spacer · divider. Data-driven blocks read ctx:
// ctx.prices { productId → { price, currency, rrp, from } } (the recipient's own wholesale prices),
// ctx.bestSellers { mine, items } (their most-ordered products, or the store's popular ones). Every block may carry `s` (style: pt/pb/px padding,
// bg, align, hide 'mobile'|'desktop') and `only` ({ currencies: [...] } — shown only to recipients
// who buy in those currencies).
// Merge fields (subject, preheader, text, buttons): {contactName} {firstName} {company} {senderName}.
import { sanitizeRich, blockRich, richToText, safeHref } from './marketingRich.js';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const fill = (tmpl, vars) => String(tmpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
const safeUrl = (u) => (/^https?:\/\//i.test(String(u || '')) ? String(u) : null);
const num = (v, d, min = 0, max = 2000) => (Number.isFinite(Number(v)) && v !== '' && v != null ? Math.min(max, Math.max(min, Number(v))) : d);
const COLOR = /^#[0-9a-fA-F]{3,8}$/;
const col = (v, d) => (COLOR.test(String(v || '')) ? v : d);

/** The TEAMM8 logo sized for email (a static file the app serves, like the site's own logo). */
export const BRAND_LOGO = '/brand/teamm8-logo-email.png';

/** Uploaded images (/api/public/media/:id) and brand files (/brand/…) live on the app; anything else
 *  must be an absolute https URL. */
export function imageUrl(u, ctx = {}) {
  const s = String(u || '').trim();
  if (/^\/api\/public\/media\/[\w.-]+$/.test(s) || /^\/brand\/[\w.-]+\.png$/.test(s)) return `${String(ctx.appUrl || '').replace(/\/$/, '')}${s}`;
  return safeUrl(s);
}

function money(n, cur, whole = false) {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency: cur || 'USD', maximumFractionDigits: whole ? 0 : 2 }).format(n);
  } catch {
    return `${n} ${cur || ''}`.trim();
  }
}
const day = (d, tz) => new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', timeZone: tz || 'UTC' });

// --- theme ------------------------------------------------------------------------------------------

export const FONTS = {
  system: { label: 'System (clean sans)', stack: "-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif" },
  helvetica: { label: 'Helvetica', stack: "'Helvetica Neue',Helvetica,Arial,sans-serif" },
  arial: { label: 'Arial', stack: 'Arial,Helvetica,sans-serif' },
  verdana: { label: 'Verdana', stack: 'Verdana,Geneva,sans-serif' },
  trebuchet: { label: 'Trebuchet', stack: "'Trebuchet MS',Helvetica,sans-serif" },
  georgia: { label: 'Georgia (serif)', stack: "Georgia,'Times New Roman',serif" },
  times: { label: 'Times', stack: "'Times New Roman',Times,serif" },
  inter: { label: 'Inter (web font)', stack: "Inter,-apple-system,'Segoe UI',Helvetica,Arial,sans-serif", web: 'Inter:wght@400;600;700' },
  montserrat: { label: 'Montserrat (web font)', stack: "Montserrat,'Helvetica Neue',Arial,sans-serif", web: 'Montserrat:wght@400;600;700' },
  playfair: { label: 'Playfair Display (web font)', stack: "'Playfair Display',Georgia,serif", web: 'Playfair+Display:wght@400;700' },
  lora: { label: 'Lora (web font)', stack: "Lora,Georgia,serif", web: 'Lora:wght@400;700' },
};
const font = (k) => (FONTS[k] || FONTS.system).stack;

export const DEFAULT_THEME = {
  bg: '#F7F8FA', // page behind the email
  contentBg: '#FFFFFF',
  text: '#334155',
  heading: '#0F172A',
  muted: '#64748B',
  line: '#E3E7EE',
  accent: '#111827', // buttons
  buttonText: '#FFFFFF',
  link: '#111827',
  font: 'system',
  headingFont: 'system',
  fontSize: 15,
  radius: 14, // content card corners
  buttonRadius: 8,
  width: 600,
  border: true,
  header: { show: true, logoUrl: BRAND_LOGO, logoWidth: 120, text: 'TEAMM8', subtext: 'Wholesale', align: 'left', link: '' },
  footerNote: '',
};

/** The campaign's theme, with every value checked (colours are hex, sizes clamped). */
export function resolveTheme(style) {
  const s = style && typeof style === 'object' ? style : {};
  const d = DEFAULT_THEME;
  const h = { ...d.header, ...(s.header || {}) };
  return {
    bg: col(s.bg, d.bg),
    contentBg: col(s.contentBg, d.contentBg),
    text: col(s.text, d.text),
    heading: col(s.heading, d.heading),
    muted: col(s.muted, d.muted),
    line: col(s.line, d.line),
    accent: col(s.accent, d.accent),
    buttonText: col(s.buttonText, d.buttonText),
    link: col(s.link, d.link),
    font: FONTS[s.font] ? s.font : d.font,
    headingFont: FONTS[s.headingFont] ? s.headingFont : d.headingFont,
    fontSize: num(s.fontSize, d.fontSize, 12, 20),
    radius: num(s.radius, d.radius, 0, 30),
    buttonRadius: num(s.buttonRadius, d.buttonRadius, 0, 40),
    width: num(s.width, d.width, 480, 700),
    border: s.border === undefined ? d.border : Boolean(s.border),
    header: {
      show: h.show !== false,
      logoUrl: String(h.logoUrl || ''),
      logoWidth: num(h.logoWidth, d.header.logoWidth, 40, 400),
      text: String(h.text ?? d.header.text).slice(0, 60),
      subtext: String(h.subtext ?? d.header.subtext).slice(0, 60),
      align: ['left', 'center', 'right'].includes(h.align) ? h.align : 'left',
      link: String(h.link || ''),
    },
    footerNote: String(s.footerNote || '').slice(0, 400),
  };
}

// Default padding per block (top, bottom; sides default to 32).
const PAD = { bestsellers: [12, 10], countdown: [12, 12], heading: [12, 4], text: [6, 6], button: [10, 14], image: [10, 10], split: [12, 12], columns: [12, 12], products: [10, 10], promotion: [10, 10], lookbook: [6, 10], video: [10, 10], quote: [12, 12], social: [12, 12], spacer: [0, 0], divider: [12, 12] };
export const blockPadding = (b) => {
  const s = b?.s || {};
  const [t, bt] = PAD[b?.type] || [8, 8];
  return { pt: num(s.pt, t, 0, 120), pb: num(s.pb, bt, 0, 120), px: num(s.px, 32, 0, 80) };
};
const alignOf = (b, d = 'left') => (['left', 'center', 'right'].includes(b?.s?.align) ? b.s.align : d);

// --- pieces -------------------------------------------------------------------------------------------

const P_MARGIN = 'margin:0 0 14px;';
function rich(html, T, vars, { size, color, lh = 1.65, align } = {}) {
  const deco = (tag) => {
    switch (tag) {
      case 'p':
        return `${P_MARGIN}font-size:${size}px;line-height:${lh};color:${color};${align ? `text-align:${align};` : ''}`;
      case 'a':
        return `color:${T.link};text-decoration:underline;`;
      case 'ul':
      case 'ol':
        return `margin:0 0 14px;padding:0 0 0 22px;font-size:${size}px;line-height:${lh};color:${color};list-style:${tag === 'ul' ? 'disc' : 'decimal'};`;
      case 'li':
        return 'margin:0 0 4px;';
      default:
        return '';
    }
  };
  // Escape merge values: they're inserted into HTML.
  const safeVars = Object.fromEntries(Object.entries(vars).map(([k, v]) => [k, esc(v)]));
  // Links first (URL-encoded values, re-checked), then the visible text (HTML-escaped values).
  let out = sanitizeRich(html, deco).replace(/href="([^"]*)"/g, (m, h) => `href="${esc(fillUrl(h.replace(/&amp;/g, '&'), vars) || '#')}"`);
  out = fill(out, safeVars);
  // The last paragraph/list doesn't need bottom space — the block's padding handles it.
  const i = out.lastIndexOf(P_MARGIN);
  if (i >= 0 && out.indexOf('<p', i) === -1 && out.indexOf('<ul', i) === -1 && out.indexOf('<ol', i) === -1) out = `${out.slice(0, i)}margin:0;${out.slice(i + P_MARGIN.length)}`;
  out = out.replace(/margin:0 0 14px;padding:0 0 0 22px;(?![\s\S]*<(p|ul|ol)\b)/, 'margin:0;padding:0 0 0 22px;');
  return `<div style="font-family:${font(T.font)};font-size:${size}px;line-height:${lh};color:${color};${align ? `text-align:${align};` : ''}">${out}</div>`;
}

/** A link with personalisation filled in URL-safely ("Smith & Co" → Smith%20%26%20Co), checked after filling. */
function fillUrl(u, vars) {
  const filled = String(u || '').replace(/\{(\w+)\}/g, (m, k) => (k in vars ? encodeURIComponent(vars[k]) : m));
  return safeHref(filled);
}

const SIZES = { sm: ['10px 18px', 13], md: ['12px 24px', 14], lg: ['15px 30px', 16] };
function buttonHtml(label, url, T, b = {}) {
  const [pad, fs] = SIZES[b.size] || SIZES.md;
  const bg = col(b.bg, T.accent);
  const fg = col(b.color, T.buttonText);
  const radius = num(b.radius, T.buttonRadius, 0, 40);
  const full = Boolean(b.full);
  const align = alignOf(b, 'left');
  return `<table role="presentation" cellpadding="0" cellspacing="0" ${full ? 'width="100%"' : ''} align="${align === 'left' ? 'left' : align === 'right' ? 'right' : 'center'}" style="${align === 'center' && !full ? 'margin:0 auto;' : ''}${full ? 'width:100%;' : ''}"><tr><td align="center" style="background:${bg};border-radius:${radius}px;mso-padding-alt:${pad};">
    <a href="${esc(url)}" style="display:${full ? 'block' : 'inline-block'};padding:${pad};font-family:${font(T.font)};font-size:${fs}px;font-weight:600;line-height:1.2;color:${fg};text-decoration:none;border-radius:${radius}px;">${esc(label)}</a>
  </td></tr></table>`;
}

function imgTag(src, alt, width, T, { radius = 8, link, fullWidth = true } = {}) {
  const img = `<img src="${esc(src)}" alt="${esc(alt || '')}" width="${Math.round(width)}" style="display:block;${fullWidth ? 'width:100%;' : ''}max-width:${Math.round(width)}px;height:auto;border:0;outline:none;text-decoration:none;border-radius:${radius}px;">`;
  const href = safeHref(link);
  return href ? `<a href="${esc(href)}" style="text-decoration:none;">${img}</a>` : img;
}

function promotionHtml(p, ctx, T) {
  if (!p) return '';
  const cur = ctx.currency;
  const rows = (p.type === 'TIERED' ? p.tiers : [])
    .map(
      (t) => `<tr>
        <td style="padding:9px 12px;border-top:1px solid ${T.line};font-size:14px;color:${T.text};">Orders of ${esc(money(t.minSubtotal, cur, true))}+</td>
        <td style="padding:9px 12px;border-top:1px solid ${T.line};font-size:14px;font-weight:700;color:${T.heading};text-align:right;">${t.percent}% off</td>
      </tr>`
    )
    .join('');
  const when = p.endsAt
    ? `${p.startsAt && new Date(p.startsAt) > (ctx.now || new Date()) ? `From ${day(p.startsAt, p.timeZone)} until` : 'Until'} ${day(p.endsAt, p.timeZone)}`
    : p.startsAt && new Date(p.startsAt) > (ctx.now || new Date())
      ? `From ${day(p.startsAt, p.timeZone)}`
      : 'Available now';
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid ${T.line};border-radius:10px;border-collapse:separate;background:#F8FAFC;font-family:${font(T.font)};">
    <tr><td style="padding:16px 16px 10px;">
      <div style="font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:${T.muted};">Your wholesale offer</div>
      <div style="margin-top:4px;font-size:18px;font-weight:700;color:${T.heading};font-family:${font(T.headingFont)};">${esc(p.name)}</div>
      <div style="margin-top:4px;font-size:15px;color:${T.text};">${esc(offerText(p, cur))}</div>
    </td></tr>
    ${rows ? `<tr><td style="padding:0 4px 6px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0">${rows}</table></td></tr>` : ''}
    <tr><td style="padding:8px 16px 14px;font-size:13px;line-height:1.5;color:${T.muted};">${esc(when)} · applied automatically when you order on the platform — no code needed.${p.converted ? ` Amounts in ${esc(cur)} at today’s rate.` : ''}</td></tr>
  </table>`;
}

/** One-line offer, e.g. "Save up to 20% — from 10% off orders of £374 or more". */
export function offerText(p, currency) {
  if (p.type !== 'TIERED') return `${p.percent}% off every order`;
  const t = p.tiers || [];
  if (!t.length) return '';
  const first = t[0];
  const max = Math.max(...t.map((x) => x.percent));
  if (t.length === 1) return first.minSubtotal > 0 ? `${first.percent}% off orders of ${money(first.minSubtotal, currency, true)} or more` : `${first.percent}% off every order`;
  return `Save up to ${max}% — from ${first.percent}% off orders of ${money(first.minSubtotal, currency, true)} or more`;
}

/** A grid of cells that stack into one column on phones. */
function grid(cells, perRow, inner, gap = 12) {
  const w = Math.floor(100 / perRow);
  const rows = [];
  for (let i = 0; i < cells.length; i += perRow) {
    const row = cells.slice(i, i + perRow);
    while (row.length < perRow) row.push(null);
    rows.push(
      `<tr>${row
        .map((c, j) => `<td class="t8-stack t8-stack-pad" width="${w}%" valign="top" style="width:${w}%;padding:0 ${j < perRow - 1 ? gap / 2 : 0}px ${gap}px ${j > 0 ? gap / 2 : 0}px;">${c ? inner(c) : ''}</td>`)
        .join('')}</tr>`
    );
  }
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0">${rows.join('')}</table>`;
}

const NETWORKS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  x: 'X',
  pinterest: 'Pinterest',
  website: 'Website',
};
export const SOCIAL_NETWORKS = NETWORKS;

/** Link to the product's page on the wholesale platform: the catalogue opened on that exact style +
 *  colour (?product=), with the style as a search fallback if it isn't in that client's range. */
export function productLink(i, ctx) {
  const id = String(i.id || '').split('/').pop();
  const style = encodeURIComponent(String(i.style || i.title || '').split(' - ')[0]);
  return `${String(ctx.appUrl || '').replace(/\/$/, '')}/catalog?${id ? `product=${encodeURIComponent(id)}&` : ''}q=${style}`;
}

/** Product cards: image, title, (their wholesale price · RRP), optional link line. */
/**
 * Fluid tiles (the "hybrid" email pattern): inline-block tiles sit `per` across on desktop and
 * wrap to two per row on phones (one-per-row stays one). Outlook desktop ignores inline-block
 * widths, so it gets a fixed table through conditional comments instead.
 */
function tileGrid(items, per, width, align, inner) {
  const tileW = Math.floor(width / per);
  const cls = per > 1 ? 't8-tile' : '';
  const cells = items
    .map(
      (c, i) =>
        `${i && i % per === 0 ? '<!--[if mso]></tr><tr><![endif]-->' : ''}<!--[if mso]><td width="${tileW}" valign="top"><![endif]--><div class="${cls}" style="display:inline-block;vertical-align:top;width:${(100 / per).toFixed(3)}%;font-size:14px;line-height:1.4;text-align:left;"><div style="padding:0 5px 16px;">${inner(c, tileW - 10)}</div></div><!--[if mso]></td><![endif]-->`
    )
    .join('');
  return `<div style="font-size:0;line-height:0;text-align:${align === 'center' ? 'center' : align === 'right' ? 'right' : 'left'};"><!--[if mso]><table role="presentation" width="${width}" cellpadding="0" cellspacing="0"><tr><![endif]-->${cells}<!--[if mso]></tr></table><![endif]--></div>`;
}

function productGrid(items, b, ctx, T, align) {
  const per = [1, 2, 3].includes(Number(b.perRow)) ? Number(b.perRow) : 2;
  const f = font(T.font);
  return tileGrid(items, per, innerWidth(b, T), align, (i, w) => {
    const link = productLink(i, ctx);
    const price =
      b.showPrice && i.price != null
        ? `<div style="margin-top:3px;font-family:${f};font-size:13px;color:${T.text};text-align:${align};"><strong style="color:${T.heading};">${i.from ? 'From ' : ''}${esc(money(i.price, i.currency || ctx.currency))}</strong> wholesale${i.rrp ? ` <span style="color:${T.muted};">· RRP ${esc(money(i.rrp, i.currency || ctx.currency))}</span>` : ''}</div>`
        : '';
    return [
      imageUrl(i.image, ctx) ? imgTag(imageUrl(i.image, ctx), i.title, w, T, { radius: num(b.radius, 8, 0, 40), link }) : '',
      `<div style="margin-top:8px;font-family:${f};font-size:13px;line-height:1.4;text-align:${align};"><a href="${esc(link)}" style="color:${T.heading};text-decoration:none;">${esc(i.title)}</a></div>`,
      price,
      b.buttonLabel ? `<div style="margin-top:4px;font-family:${f};font-size:13px;text-align:${align};"><a href="${esc(link)}" style="color:${T.link};font-weight:600;text-decoration:underline;">${esc(b.buttonLabel)}</a></div>` : '',
    ].join('');
  });
}

// --- blocks ---------------------------------------------------------------------------------------

/** Inner width (px) a block's content has, for image sizing. */
const innerWidth = (b, T) => T.width - 2 * blockPadding(b).px;

/** A block's inner HTML (no padding wrapper). Empty string = nothing to show. */
export function blockInner(b, ctx, vars, T) {
  const align = alignOf(b);
  const hf = font(T.headingFont);
  switch (b?.type) {
    case 'heading': {
      const level = [1, 2, 3].includes(Number(b.level)) ? Number(b.level) : 1;
      const size = num(b.size, { 1: 26, 2: 21, 3: 17 }[level], 12, 56);
      const color = col(b.color, T.heading);
      const deco = (tag) => (tag === 'a' ? `color:${T.link};text-decoration:underline;` : '');
      const safeVars = Object.fromEntries(Object.entries(vars).map(([k, v]) => [k, esc(v)]));
      // Headings are one paragraph: flatten <p>s to line breaks.
      const inner = fill(sanitizeRich(blockRich(b), deco), safeVars)
        .replace(/<\/p>\s*<p[^>]*>/g, '<br>')
        .replace(/<\/?(p|ul|ol|li)[^>]*>/g, '');
      if (!inner.replace(/<br>/g, '').trim()) return '';
      return `<h${level} style="margin:0;font-family:${hf};font-size:${size}px;line-height:1.25;font-weight:700;color:${color};text-align:${align};letter-spacing:-0.01em;">${inner}</h${level}>`;
    }
    case 'text': {
      const html = blockRich(b);
      if (!richToText(html)) return '';
      return rich(html, T, vars, { size: num(b.size, T.fontSize, 11, 24), color: col(b.color, T.text), align: align === 'left' ? null : align });
    }
    case 'button': {
      const url = fillUrl(b.url, vars) || `${ctx.appUrl}/catalog`;
      return buttonHtml(fill(b.label || 'Place an order', vars), url, T, { ...b, s: { align: alignOf(b, 'left') } });
    }
    case 'image': {
      const src = imageUrl(b.url, ctx);
      if (!src) return '';
      const pct = num(b.width, 100, 10, 100);
      const w = (innerWidth(b, T) * pct) / 100;
      const img = imgTag(src, b.alt, w, T, { radius: num(b.radius, 8, 0, 40), link: b.link });
      return `<table role="presentation" width="${pct}%" cellpadding="0" cellspacing="0" align="${align}" style="width:${pct}%;${align === 'center' ? 'margin:0 auto;' : ''}"><tr><td>${img}</td></tr></table>`;
    }
    case 'split': {
      const src = imageUrl(b.image?.url, ctx);
      const w = innerWidth(b, T);
      const imgCell = src ? imgTag(src, b.image?.alt, w / 2 - 10, T, { radius: num(b.radius, 8, 0, 40), link: b.image?.link }) : '';
      const textCell = [
        richToText(blockRich(b)) ? rich(blockRich(b), T, vars, { size: num(b.size, T.fontSize, 11, 24), color: T.text }) : '',
        b.button?.label ? `<div style="height:14px;line-height:14px;">&nbsp;</div>${buttonHtml(fill(b.button.label, vars), fillUrl(b.button.url, vars) || `${ctx.appUrl}/catalog`, T, { size: 'sm', s: { align: 'left' } })}` : '',
      ].join('');
      if (!imgCell && !textCell) return '';
      const cells = b.side === 'right' ? [textCell, imgCell] : [imgCell, textCell];
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
        ${cells.map((c, i) => `<td class="t8-stack t8-stack-pad" width="50%" valign="${b.valign === 'top' ? 'top' : 'middle'}" style="width:50%;padding:0 ${i === 0 ? 10 : 0}px 0 ${i === 1 ? 10 : 0}px;">${c}</td>`).join('')}
      </tr></table>`;
    }
    case 'columns': {
      const cols = (b.cols || []).slice(0, 3);
      if (!cols.length) return '';
      const per = cols.length;
      const w = (innerWidth(b, T) - 12 * (per - 1)) / per;
      return grid(cols, per, (c) => {
        const src = imageUrl(c.image, ctx);
        return [
          src ? imgTag(src, c.alt, w, T, { radius: num(b.radius, 8, 0, 40), link: c.link || c.url }) : '',
          c.title ? `<div style="margin-top:${src ? 12 : 0}px;font-family:${hf};font-size:16px;font-weight:700;line-height:1.3;color:${T.heading};text-align:${align};">${esc(fill(c.title, vars))}</div>` : '',
          richToText(c.html || '') ? `<div style="margin-top:6px;">${rich(c.html, T, vars, { size: 14, color: T.text, lh: 1.55, align: align === 'left' ? null : align })}</div>` : '',
          c.label ? `<div style="margin-top:12px;">${buttonHtml(fill(c.label, vars), fillUrl(c.url, vars) || `${ctx.appUrl}/catalog`, T, { size: 'sm', s: { align } })}</div>` : '',
        ].join('');
      });
    }
    case 'products': {
      let items = (b.items || []).filter((i) => i && (i.title || i.image));
      // With prices on, show each client only what's in THEIR catalogue (when we know it). The
      // builder's canvas always shows every pick (ctx.editing) — Review shows each client's version.
      if (b.showPrice && ctx.prices && !ctx.editing) items = items.filter((i) => ctx.prices[i.id]);
      items = items.slice(0, Math.min(12, Number(b.count) || 12));
      if (!items.length) return '';
      const title = b.title ? `<div style="margin:0 0 12px;font-family:${hf};font-size:18px;font-weight:700;color:${T.heading};text-align:${align};">${esc(fill(b.title, vars))}</div>` : '';
      return title + productGrid(items.map((i) => ({ ...i, ...(b.showPrice ? ctx.prices?.[i.id] || {} : {}) })), b, ctx, T, align);
    }
    case 'bestsellers': {
      const bs = ctx.bestSellers;
      if (!bs?.items?.length || (!bs.mine && b.fallback === 'hide')) return '';
      const head = bs.mine ? b.title || 'Your best sellers — time to restock?' : b.altTitle || 'Popular with stockists right now';
      const intro = bs.mine ? b.intro : b.altIntro;
      return `<div style="margin:0 0 ${intro ? 6 : 12}px;font-family:${hf};font-size:18px;font-weight:700;color:${T.heading};text-align:${align};">${esc(fill(head, vars))}</div>
        ${intro ? `<div style="margin:0 0 14px;font-family:${font(T.font)};font-size:14px;line-height:1.5;color:${T.text};text-align:${align};">${esc(fill(intro, vars))}</div>` : ''}
        ${productGrid(bs.items, { ...b, showPrice: b.showPrice !== false }, ctx, T, align)}`;
    }
    case 'countdown': {
      const ends = b.source === 'promotion' ? ctx.promotion?.endsAt : b.endsAt;
      const t = ends ? Math.floor(new Date(ends).getTime() / 1000) : NaN;
      if (!Number.isFinite(t)) return '';
      // The image endpoint takes 6-digit hex: expand #FFF, drop any alpha.
      const hexOf = (v) => {
        let h = String(v).replace('#', '');
        if (h.length === 3 || h.length === 4) h = [...h.slice(0, 3)].map((x) => x + x).join('');
        return h.slice(0, 6);
      };
      const src = `${String(ctx.appUrl || '').replace(/\/$/, '')}/api/public/countdown.gif?to=${t}&bg=${hexOf(col(b.s?.bg, T.contentBg))}&box=${hexOf(col(b.box, T.accent))}&fg=${hexOf(col(b.color, T.buttonText))}&r=${num(b.radius, 10, 0, 30)}`;
      const w = Math.min(520, innerWidth(b, T));
      return `${b.heading ? `<div style="margin:0 0 10px;font-family:${hf};font-size:15px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:${T.heading};text-align:center;">${esc(fill(b.heading, vars))}</div>` : ''}
        <img src="${esc(src)}" alt="${esc(`Ends ${day(ends, ctx.promotion?.timeZone)}`)}" width="${w}" style="display:block;margin:0 auto;width:100%;max-width:${w}px;height:auto;border:0;">
        ${b.caption !== false ? `<div style="margin-top:8px;font-family:${font(T.font)};font-size:13px;color:${T.muted};text-align:center;">Ends ${esc(day(ends, ctx.promotion?.timeZone))}</div>` : ''}`;
    }
    case 'promotion':
      return promotionHtml(ctx.promotion, ctx, T);
    case 'lookbook':
      return ctx.lookbookUrl ? buttonHtml(b.label || 'View the lookbook (PDF)', ctx.lookbookUrl, T, { ...b, s: { align: alignOf(b, 'left') } }) : '';
    case 'video': {
      const href = safeHref(b.url);
      const thumb = imageUrl(b.thumbnail, ctx);
      if (!href || !thumb) return '';
      return `${imgTag(thumb, b.alt || 'Watch the video', innerWidth(b, T), T, { radius: num(b.radius, 8, 0, 40), link: href })}
        <div style="margin-top:10px;font-family:${font(T.font)};font-size:14px;text-align:${align};"><a href="${esc(href)}" style="color:${T.link};font-weight:600;text-decoration:none;">&#9654;&nbsp; ${esc(b.label || 'Watch the video')}</a></div>`;
    }
    case 'quote': {
      const html = blockRich(b);
      if (!richToText(html)) return '';
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td style="border-left:3px solid ${col(b.color, T.accent)};padding:2px 0 2px 18px;">
        ${rich(html, T, vars, { size: 17, color: T.heading, lh: 1.55 })}
        ${b.author ? `<div style="margin-top:10px;font-family:${font(T.font)};font-size:13px;color:${T.muted};">— ${esc(fill(b.author, vars))}</div>` : ''}
      </td></tr></table>`;
    }
    case 'social': {
      const links = (b.links || []).filter((l) => NETWORKS[l?.network] && safeUrl(l.url));
      if (!links.length) return '';
      const pills = links
        .map((l) => `<a href="${esc(l.url)}" style="display:inline-block;margin:0 4px 6px;padding:7px 14px;border:1px solid ${T.line};border-radius:999px;font-family:${font(T.font)};font-size:13px;font-weight:600;color:${T.heading};text-decoration:none;">${esc(NETWORKS[l.network])}</a>`)
        .join('');
      return `<div style="text-align:${alignOf(b, 'center')};">${pills}</div>`;
    }
    case 'spacer':
      return `<div style="height:${num(b.height, 24, 4, 200)}px;line-height:${num(b.height, 24, 4, 200)}px;font-size:1px;">&nbsp;</div>`;
    case 'divider': {
      const pct = num(b.width, 100, 10, 100);
      return `<table role="presentation" width="${pct}%" cellpadding="0" cellspacing="0" align="center" style="width:${pct}%;margin:0 auto;"><tr><td style="border-top:${num(b.thickness, 1, 1, 8)}px ${b.dashed ? 'dashed' : 'solid'} ${col(b.color, T.line)};font-size:1px;line-height:1px;">&nbsp;</td></tr></table>`;
    }
    default:
      return '';
  }
}

/** Is this block shown to this recipient? (Only-for-currencies targeting.) */
export function visibleTo(b, ctx) {
  const cur = b?.only?.currencies;
  if (!Array.isArray(cur) || !cur.length || !ctx?.currency) return true;
  return cur.includes(ctx.currency);
}

/** A block as a table row: padding, background, mobile/desktop visibility. */
export function blockRow(b, ctx, vars, T, { always = false } = {}) {
  const inner = blockInner(b, ctx, vars, T);
  if (!inner && !always) return '';
  const { pt, pb, px } = blockPadding(b);
  const bg = col(b?.s?.bg, '');
  const hide = b?.s?.hide;
  const trAttr = hide === 'mobile' ? ' class="t8-hide-m"' : hide === 'desktop' ? ' class="t8-show-m" style="display:none;max-height:0;overflow:hidden;mso-hide:all;"' : '';
  const row = `<tr${trAttr}><td class="${px >= 24 ? 't8-px' : ''}" style="padding:${pt}px ${px}px ${pb}px;${bg ? `background:${bg};` : ''}">${inner}</td></tr>`;
  // Phone-only rows are left out of Outlook desktop entirely (it ignores display:none on nested tables).
  return hide === 'desktop' ? `<!--[if !mso]><!-->${row}<!--<![endif]-->` : row;
}

export function headerHtml(T, ctx = {}) {
  const h = T.header;
  if (!h.show) return '';
  const logo = imageUrl(h.logoUrl, ctx);
  const label = h.subtext ? `<span style="font-family:${font(T.font)};font-size:15px;font-weight:500;color:${T.muted};">${esc(h.subtext)}</span>` : '';
  const img = logo ? `<img src="${esc(logo)}" alt="${esc(h.text || 'TEAMM8')}" width="${h.logoWidth}" style="display:block;width:${h.logoWidth}px;max-width:100%;height:auto;border:0;">` : '';
  // Logo + optional label side by side (like the platform's own header).
  const inner = logo
    ? `<table role="presentation" cellpadding="0" cellspacing="0" align="${h.align}" style="${h.align === 'center' ? 'margin:0 auto;' : ''}"><tr><td valign="middle">${img}</td>${label ? `<td valign="middle" style="padding-left:10px;">${label}</td>` : ''}</tr></table>`
    : `<span style="font-family:${font(T.headingFont)};font-size:19px;font-weight:700;letter-spacing:.04em;color:${T.heading};">${esc(h.text)}${h.subtext ? ` <span style="font-weight:400;color:${T.muted};">${esc(h.subtext)}</span>` : ''}</span>`;
  const href = safeUrl(h.link);
  return `<tr><td align="${h.align}" style="padding:0 8px 18px;text-align:${h.align};">${href ? `<a href="${esc(href)}" style="text-decoration:none;">${inner}</a>` : inner}</td></tr>`;
}

export const RESPONSIVE_CSS = `@media only screen and (max-width:480px){
  .t8-wrap{padding:16px 8px !important}
  .t8-stack{display:block !important;width:100% !important;max-width:100% !important;padding-left:0 !important;padding-right:0 !important}
  .t8-px{padding-left:20px !important;padding-right:20px !important}
  .t8-tile{width:50% !important;max-width:50% !important}
  .t8-hide-m{display:none !important}
  .t8-show-m{display:table-row !important;max-height:none !important;overflow:visible !important}
}`;

export function mergeVars(ctx = {}) {
  const first = String(ctx.contactName || '').trim().split(/\s+/)[0];
  return {
    contactName: ctx.contactName || 'there',
    firstName: first || 'there',
    company: ctx.company || 'your store',
    senderName: ctx.senderName || 'The TEAMM8 team',
  };
}
export const MERGE_FIELDS = ['firstName', 'contactName', 'company', 'senderName'];

function blockText(b, ctx, vars) {
  switch (b?.type) {
    case 'heading':
    case 'text':
      return fill(richToText(blockRich(b)), vars);
    case 'quote':
      return `“${fill(richToText(blockRich(b)), vars)}”${b.author ? ` — ${fill(b.author, vars)}` : ''}`;
    case 'button':
      return `${fill(b.label || 'Place an order', vars)}: ${safeHref(b.url) || `${ctx.appUrl}/catalog`}`;
    case 'split':
      return [fill(richToText(blockRich(b)), vars), b.button?.label ? `${fill(b.button.label, vars)}: ${safeHref(b.button.url) || `${ctx.appUrl}/catalog`}` : ''].filter(Boolean).join('\n');
    case 'columns':
      return (b.cols || [])
        .map((c) => [c.title, richToText(c.html || ''), c.label ? `${c.label}: ${safeHref(c.url) || `${ctx.appUrl}/catalog`}` : ''].filter(Boolean).map((x) => fill(x, vars)).join('\n'))
        .join('\n\n');
    case 'promotion': {
      const p = ctx.promotion;
      if (!p) return '';
      const tiers = p.type === 'TIERED' ? p.tiers.map((t) => `  Orders of ${money(t.minSubtotal, ctx.currency, true)}+: ${t.percent}% off`).join('\n') : '';
      return `${p.name}\n${offerText(p, ctx.currency)}${tiers ? `\n${tiers}` : ''}\nApplied automatically when you order on the platform.`;
    }
    case 'products': {
      const items = (b.items || []).filter((i) => !(b.showPrice && ctx.prices) || ctx.prices[i.id]).slice(0, Math.min(12, Number(b.count) || 12));
      return [b.title, ...items.map((i) => `- ${i.title}${b.showPrice && ctx.prices?.[i.id] ? ` — ${money(ctx.prices[i.id].price, ctx.prices[i.id].currency)} wholesale` : ''}`)].filter(Boolean).join('\n');
    }
    case 'bestsellers': {
      const bs = ctx.bestSellers;
      if (!bs?.items?.length || (!bs.mine && b.fallback === 'hide')) return '';
      return [bs.mine ? b.title || 'Your best sellers — time to restock?' : b.altTitle || 'Popular with stockists right now', ...bs.items.map((i) => `- ${i.title}${i.price != null ? ` — ${money(i.price, i.currency)} wholesale` : ''}`)].map((x) => fill(x, vars)).join('\n');
    }
    case 'countdown': {
      const ends = b.source === 'promotion' ? ctx.promotion?.endsAt : b.endsAt;
      return ends ? `${b.heading ? `${fill(b.heading, vars)} — ` : ''}ends ${day(ends, ctx.promotion?.timeZone)}` : '';
    }
    case 'lookbook':
      return ctx.lookbookUrl ? `${b.label || 'View the lookbook (PDF)'}: ${ctx.lookbookUrl}` : '';
    case 'video':
      return safeHref(b.url) ? `${b.label || 'Watch the video'}: ${b.url}` : '';
    case 'social':
      return (b.links || []).filter((l) => NETWORKS[l?.network] && safeUrl(l.url)).map((l) => `${NETWORKS[l.network]}: ${l.url}`).join('\n');
    default:
      return '';
  }
}

/**
 * Render a campaign for one recipient.
 * campaign: { subject, preheader, blocks, style }
 * ctx: { contactName, company, senderName, currency, promotion, lookbookUrl, unsubscribeUrl, appUrl, now }
 */
export function renderCampaign(campaign, ctx) {
  const vars = mergeVars(ctx);
  const T = resolveTheme(campaign.style);
  const blocks = (Array.isArray(campaign.blocks) ? campaign.blocks : []).filter((b) => visibleTo(b, ctx));
  const subject = fill(campaign.subject, vars).trim() || 'News from TEAMM8 Wholesale';
  const preheader = fill(campaign.preheader || '', vars);
  const rows = blocks.map((b) => blockRow(b, ctx, vars, T)).join('\n');
  const webFonts = [...new Set([T.font, T.headingFont].map((k) => FONTS[k]?.web).filter(Boolean))];
  const unsub = ctx.unsubscribeUrl ? `<a href="${esc(ctx.unsubscribeUrl)}" style="color:${T.muted};text-decoration:underline;">Unsubscribe from marketing emails</a>` : '';
  const card = T.border ? `border:1px solid ${T.line};` : '';
  const html = `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="x-apple-disable-message-reformatting"><title>${esc(subject)}</title>
${webFonts.length ? `<link href="https://fonts.googleapis.com/css2?${webFonts.map((f) => `family=${f}`).join('&')}&display=swap" rel="stylesheet">` : ''}
<style>${RESPONSIVE_CSS}</style>
${webFonts.length ? '<!--[if mso]><style>body,table,td,div,p,a,span,h1,h2,h3{font-family:Arial,Helvetica,sans-serif !important}</style><![endif]-->' : ''}</head>
<body style="margin:0;padding:0;background:${T.bg};font-family:${font(T.font)};color:${T.heading};-webkit-text-size-adjust:100%;">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;">${esc(preheader)}&#8203;&#8203;&#8203;&#8203;</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${T.bg};"><tr><td class="t8-wrap" align="center" style="padding:32px 12px;">
  <table role="presentation" width="${T.width}" cellpadding="0" cellspacing="0" style="width:100%;max-width:${T.width}px;">
    ${headerHtml(T, ctx)}
    <tr><td style="background:${T.contentBg};${card}border-radius:${T.radius}px;overflow:hidden;">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
        <tr><td style="height:22px;line-height:22px;font-size:1px;">&nbsp;</td></tr>
        ${rows}
        <tr><td style="height:22px;line-height:22px;font-size:1px;">&nbsp;</td></tr>
      </table>
    </td></tr>
    <tr><td style="padding:18px 8px 0;font-family:${font(T.font)};font-size:12px;line-height:1.6;color:${T.muted};text-align:${T.header.align === 'center' ? 'center' : 'left'};">
      ${T.footerNote ? `${esc(fill(T.footerNote, vars))}<br>` : ''}You’re receiving this because ${esc(vars.company)} is a TEAMM8 wholesale customer. Replies go straight to ${esc(vars.senderName)}.<br>
      TEAMM8 Wholesale · Sydney, Australia${unsub ? ` · ${unsub}` : ''}
    </td></tr>
  </table>
</td></tr></table>
</body></html>`;
  const text = [...blocks.map((b) => blockText(b, ctx, vars)).filter(Boolean), '', ctx.unsubscribeUrl ? `Unsubscribe from marketing emails: ${ctx.unsubscribeUrl}` : '']
    .join('\n\n')
    .trim();
  return { subject, preheader, html, text };
}
